<template>
    <div class="nav-upgrade-to-unlock">
        <div>
            <div class="publish">
                <Cta :type="isDraft ? 'primary' : 'primary subtle'"
                     :class="{'right-icon':isDraft}"
                     dark-scheme
                     size="small"
                     @click.stop="handlePublishClick"
                     :loading="loading && !isDraft"
                >
                    <div class="btn-content">
                        {{ getPublishButtonText }}
                        <Icon v-if="isDraft" :width="18" :height="18" icon-name="chevron-down" />
                    </div>
                </Cta>
                <div v-if="showPublishTooltip" @click="$store.state.showPublishTooltip = false" class="tooltip-bg" />
                <LargeTooltip v-if="showPublishTooltip" title="Ready to go live?" subtitle="When you’re ready, publish your portfolio and share it with the world!" arrow-pos="top top-right"/>
                <ChecklistTooltip v-if="!upgradeDropdownOpen && $store.state.checklist.activeInteraction === ChecklistInteractionEnum.PUBLISH"/>
            </div>
        </div>
        <transition name="drop-down-from-toolbar">
            <div v-if="upgradeDropdownOpen" ref="upgradepopup" class="upgrade-to-unlock-container">
                <template v-if="blocked">
                    <div class="menu-item">
                        <div class="title-text">Ready to publish</div>
                        <div class="input-container">
                            <InlineUrlEditInput />
                            <Cta dark-scheme type="upgrade" @click="openPrices">Upgrade to publish</Cta>
                        </div>
                    </div>
                </template>
                <template v-else>
                    <div class="menu-item">
                        <div class="title-text">Ready to publish</div>
                        <div class="input-container">
                            <InlineUrlEditInput v-if="!hasOwnDomain" />
                            <div class="own-domain-container" v-else>
                                <Icon icon-name="link" class="leading-icon" />
                                {{ portfolioURL }}
                            </div>
                            <Cta dark-scheme type="primary" @click="publish">Publish</Cta>
                        </div>
                    </div>
                </template>
                <div class="dd-container">
                    <div class="dd-select-items">
                        <a :class="['dd-select-item', { 'blocked': blocked }]"
                           @click="openDomainPurchase">
                            <Icon :height="24" :width="24" :icon-name="'web'"/>
                            Get a custom domain
                            <Tag
                                v-if="blocked"
                                icon-name="lock"
                                text="Upgrade to unlock"
                                dark-scheme
                                @click.stop="openPrices"
                            />
                        </a>
                    </div>
                </div>

            </div>
        </transition>
    </div>
</template>

<script>
import Icon from "../../common/Icon.vue";
import Tag from "../components/Tag.vue";
import Cta from "../components/CtaButton.vue";
import LargeTooltip from "../components/LargeTooltip.vue";
import MixpanelService from "../../analytics/mixpanel.js";
import {ChecklistInteractionEnum, Features} from "../enums.js";
import {isFeatureAvailable} from "../helpers.js";
import ChecklistTooltip from "@/scripts/common/activationChecklist/component/ChecklistTooltip.vue";
import InlineUrlEditInput from "@/scripts/common/components/InlineUrlEditInput.vue";

export default {
    name: "UpgradeToUnlockNavContainer",
    props: [ "openNavigationDropdown", "upgradeDropdownOpen", 'isViewMode'],
    components: {
        InlineUrlEditInput,
        ChecklistTooltip,
        Cta,
        Tag,
        Icon,
        LargeTooltip
    },
    data() {
        return {
            copied: false,
            timeoutId: null,
            loading: false
        }
    },
    beforeDestroy() {
        clearInterval(this.timeoutId);
        this.loading = false;
    },
    watch: {
        upgradeDropdownOpen() {
            this.handleEventListener();
        }
    },
    methods: {
        handleClickOutside(event) {
            // Check if click was outside the popup
            if (this.upgradeDropdownOpen && this.$refs.upgradepopup && !this.$refs.upgradepopup.contains(event.target)) {
                this.loading = false
                this.openNavigationDropdown("upgrade");
            }
        },
        handleEventListener() {
            if(this.upgradeDropdownOpen) {
                document.addEventListener('click', this.handleClickOutside);
            }
            else {
                document.removeEventListener('click', this.handleClickOutside);
            }
        },
        openPrices() {
            MixpanelService.track("UPGRADE_CTA_CLICKED",
                {
                    ...MixpanelService.getUrls(),
                    "event_group": MixpanelService.MixpanelEventGroups.USER_LIFECYCLE,
                    "source": "Portfolio publish panel",
                    "context": `${this.isViewMode ? 'Preview mode' : 'Editor'}`,
                    "to": "Payment"
                }
            )
            if(this.isViewMode) {
                window.location.replace('/home/prices');
            }
            else {
                this.$router.push('/home/prices');
            }
        },
        handlePublishClick() {
            this.$store.state.showPublishTooltip = false;

            if (this.isDraft) {
                MixpanelService.track(
                    "PUBLISH_CTA_CLICKED",
                    {
                        ...MixpanelService.getUrls(),
                        "event_group": MixpanelService.MixpanelEventGroups.USER_LIFECYCLE,
                        "upgrade_required": this.blocked,
                        "context": `${this.isViewMode ? 'Preview mode' : 'Editor'}`,
                    }
                )
            }

            this.loading = true;
            if(!this.isDraft) {
                this.publish();
            }
            else {
                this.openNavigationDropdown("upgrade");
            }

        },
        closeUpgradeDropdown() {
            if (this.upgradeDropdownOpen) {
                this.openNavigationDropdown("upgrade");
            }
            this.loading = false;
        },
        publish() {
            this.loading = true;
            this.timeoutId = setTimeout(() => {this.loading = false}, 1000);
            const wasDraft = this.isDraft;

            this.$actions.switchPortfolioDraft(this.$store.state.portfolio).then(publishedStateChanged => {
                if (!publishedStateChanged) {
                    return;
                }

                if(wasDraft) {
                    MixpanelService.track(
                        "PORTFOLIO_PUBLISHED",
                        {
                            ...MixpanelService.getUrls(),
                            "event_group": MixpanelService.MixpanelEventGroups.PORTFOLIO,
                            "portfolio_id": this.$store.state.portfolio._id,
                        }
                    )
                    this.$store.commit('updateChecklistItem', ChecklistInteractionEnum.PUBLISH);
                }
                else {
                    MixpanelService.track(
                        "PORTFOLIO_UNPUBLISHED",
                        {
                            ...MixpanelService.getUrls(),
                            "event_group": MixpanelService.MixpanelEventGroups.PORTFOLIO,
                            "portfolio_id": this.$store.state.portfolio._id,
                        }
                    )
                }

                this.closeUpgradeDropdown();
                //Commented out because it was competing with the `FirstPortfolioPublishModal` modal. I wasn't sure what to do.'
                // let portfolioPublishModalOpened = localStorage.getItem('portfolioPublishModalOpened');
                // if(!portfolioPublishModalOpened && wasDraft) {
                //     this.$store.commit("setActiveModal", "FirstPortfolioPublishModal");
                //     localStorage.setItem('portfolioPublishModalOpened', true);
                // }
            });
        },
        openDomainPurchase() {
            if(this.blocked) return
            this.$router.push('/home/domain/search');
        },
    },
    computed: {
        ChecklistInteractionEnum() {
            return ChecklistInteractionEnum
        },
        blocked() {
            return !isFeatureAvailable(Features.PUBLISH_PORTFOLIO, this.$store.state.userSettings.currentSubscriptionPlan, this.$store.state.userSettings.currentSubscription?.status);
        },
        isDraft() {
            return !!(this.$store.state.portfolio.draft)
        },
        getPublishButtonText() {
            return `${this.isDraft ? "Publish" : "Unpublish"}`;
        },
        showPublishTooltip() {
            return this.$store.state.showPublishTooltip && this.isDraft;
        },
        portfolioURL() {
            return this.$store.getters.pageViewURL.replace(/^https?:\/\//, '');
        },
        hasOwnDomain() {
            return this.$store.state.portfolio.ownDomainAddress
        }
    }

}
</script>

<style scoped lang="scss">
.nav-upgrade-to-unlock {
    position: relative;

    .publish {
        margin: 0 16px 0 8px;

        .ufo-cta {
            &.right-icon {
                padding: 10px 12px 10px 16px;
            }
        }

        .tooltip-bg {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
        }
        .large-tooltip-container {
            top: 61px;
            right: 27px;
        }
    }

    .ufo-cta {
        .btn-content {
            display: flex;
            align-items: center;
            gap: 4px;
        }
    }

    .upgrade-to-unlock-container {

        position: absolute;
        right: 16px;
        top: 55px;
        display: flex;
        width: 400px;
        flex-direction: column;
        border-radius: 8px;
        border: 1px solid var(--asphalt90);
        background: var(--asphalt95);

        .menu-item {
            display: flex;
            padding: 24px;
            flex-direction: column;
            justify-content: center;
            align-items: flex-start;
            gap: 24px;
            align-self: stretch;
            border-bottom: 1px solid var(--asphalt90);

            .title-text {
                font: var(--editor-ui-b1-semibold);
                color: var(--asphalt10);
            }

            .input-container {
                display: flex;
                flex-direction: column;
                align-items: flex-start;
                gap: 16px;
                align-self: stretch;

                .own-domain-container {
                    display: flex;
                    width: 100%;
                    padding: 8px 8px 8px 12px;
                    justify-content: flex-start;
                    align-items: center;
                    border-radius: 6px;
                    border: 1px solid var(--asphalt80);
                    gap: 4px;

                    font: var(--editor-ui-b2-regular);
                    color: var(--asphalt30);

                    svg {
                        width: 20px;
                        height: 20px;
                    }
                }
            }

            button {
                width: 100%;
            }
        }

        .dd-container {
            display: flex;
            padding: 8px 0;
            align-items: center;
            gap: 20px;
            align-self: stretch;

            .dd-select-items {
                position: relative;
                width: 100%;
                display: flex;
                flex-direction: column;
            }

            .dd-select-item {
                padding: 12px 24px;
                color: var(--asphalt40);
                font: var(--editor-ui-b2-medium);
                display: flex;
                flex-direction: row;
                align-items: center;
                cursor: pointer;
                gap: 8px;

                &:not(.blocked):hover,
                &:not(.blocked):focus {
                    color: var(--asphalt10);
                }
                &.blocked {
                    cursor: default;
                    color: var(--asphalt50);
                }

                svg {
                    width: 20px;
                    height: 20px;
                }
            }
        }

    }
}
</style>